// Phase 9 R4 — Consume stock against a parts line via R1 RPC.
// Backend atomically: decrements quantity_on_hand, decrements reservation if any,
// increments parts_line.inventory_consumed_qty, writes a `consume` ledger entry.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export function useConsumeInventoryForPartsLine() {
  const { activeShop } = useAuth();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      partsLineId,
      qty,
    }: {
      partsLineId: string;
      qty: number;
      // Carried for invalidation; not part of RPC signature.
      ticketId?: string;
      inventoryItemId?: string;
    }) => {
      const { data, error } = await supabase.rpc('consume_inventory_for_parts_line', {
        p_parts_line_id: partsLineId,
        p_qty: qty,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (_data, vars) => {
      const shopId = activeShop?.id;
      if (vars.ticketId) {
        qc.invalidateQueries({ queryKey: ['parts-lines', shopId, vars.ticketId] });
      }
      if (vars.inventoryItemId) {
        qc.invalidateQueries({ queryKey: ['inventory-item', shopId, vars.inventoryItemId] });
        qc.invalidateQueries({ queryKey: ['inventory-transactions', shopId, vars.inventoryItemId] });
      }
      qc.invalidateQueries({ queryKey: ['inventory-items', shopId] });
      toast.success(`Consumed ${vars.qty}`);
    },
    onError: (e: any) => toast.error(e?.message ?? 'Consume failed'),
  });
}
